/**
 * Cardless scale: the iris is the ruler.
 *
 * Within one frame, PD_mm ≈ (IPD_px / irisDiameter_px) × irisReference_mm. Both lengths sit at nearly the same depth, so the camera's focal length and the working distance largely cancel. What does not cancel is the person's own iris: the reference is a population figure, and a person whose visible iris differs from it by x% gets a PD off by about x% the other way. That scale uncertainty is biology, not capture quality, and is reported separately from it.
 */

/** An initial engineering reference for the horizontal visible iris diameter, never a clinical truth for any one person. */
export const IRIS_REFERENCE_MM = 11.7;

/** Distance from the eye's centre of rotation to the visible iris plane, mm: textbook schematic-eye values, used only to undo convergence. */
export const ROTATION_TO_IRIS_MM = 10;

/** PD from one frame's pixel geometry and an assumed iris diameter. */
export const pdFromRatio = (
  ipdPx: number,
  irisDiameterPx: number,
  irisReferenceMm = IRIS_REFERENCE_MM,
): number => (ipdPx / irisDiameterPx) * irisReferenceMm;

/** Camera to iris-plane distance implied by the iris's pixel size, for a focal length in pixels; an estimate that carries both the iris and the field-of-view assumptions. */
export const distanceFromIris = (
  irisDiameterPx: number,
  focalPx: number,
  irisReferenceMm = IRIS_REFERENCE_MM,
): number => (focalPx * irisReferenceMm) / irisDiameterPx;

export const focalFromFov = (longSidePx: number, longSideFovDeg: number): number =>
  longSidePx / 2 / Math.tan((longSideFovDeg * Math.PI) / 360);

/**
 * PD seen while both eyes fixate a point straight ahead at `fixationMm` from the iris plane, from the distance PD: each eye turns inwards by atan(PD/2 ÷ (fixation + rotation radius)) and carries its iris centre that far across.
 */
export function convergedFromDistancePd(
  distancePdMm: number,
  fixationMm: number,
  rotationToIrisMm = ROTATION_TO_IRIS_MM,
): number {
  const D = fixationMm + rotationToIrisMm;
  return distancePdMm - 2 * rotationToIrisMm * Math.sin(Math.atan(distancePdMm / 2 / D));
}

/** The inverse: distance PD from a PD measured with the eyes on the lens. Solved by fixed-point iteration, which converges in a few steps because the correction is small. */
export function distanceFromConvergedPd(
  convergedPdMm: number,
  fixationMm: number,
  rotationToIrisMm = ROTATION_TO_IRIS_MM,
): { distancePdMm: number; correctionMm: number } {
  const D = fixationMm + rotationToIrisMm;
  let pd = convergedPdMm;
  for (let i = 0; i < 20; i += 1)
    pd = convergedPdMm + 2 * rotationToIrisMm * Math.sin(Math.atan(pd / 2 / D));
  return { distancePdMm: pd, correctionMm: pd - convergedPdMm };
}

export interface SensitivityCell {
  truePdMm: number;
  actualIrisMm: number;
  assumedIrisMm: number;
  estimatedPdMm: number;
  errorMm: number;
  irisErrorPct: number;
  pdScaleErrorPct: number;
}

/** What the iris-ratio estimate reports for someone whose visible iris is `actualIrisMm` when the algorithm assumes `assumedIrisMm`: exact, from the scale relationship alone. */
export function irisSensitivity(
  truePdMm: number,
  actualIrisMm: number,
  assumedIrisMm = IRIS_REFERENCE_MM,
): SensitivityCell {
  const estimatedPdMm = (truePdMm * assumedIrisMm) / actualIrisMm;
  return {
    truePdMm,
    actualIrisMm,
    assumedIrisMm,
    estimatedPdMm,
    errorMm: estimatedPdMm - truePdMm,
    irisErrorPct: ((actualIrisMm - assumedIrisMm) / assumedIrisMm) * 100,
    pdScaleErrorPct: (assumedIrisMm / actualIrisMm - 1) * 100,
  };
}

/** The range of actual iris diameters for which the estimate stays within ±toleranceMm at a given true PD. */
export function irisRangeWithin(
  truePdMm: number,
  toleranceMm: number,
  assumedIrisMm = IRIS_REFERENCE_MM,
): [number, number] {
  return [
    (truePdMm * assumedIrisMm) / (truePdMm + toleranceMm),
    (truePdMm * assumedIrisMm) / (truePdMm - toleranceMm),
  ];
}

/** For one capture: what the same pixels would mean for a range of possible iris sizes — the scale uncertainty, stated plainly. */
export const pdIfIrisWere = (
  estimatedPdMm: number,
  irisMm: number,
  assumedIrisMm = IRIS_REFERENCE_MM,
): number => (estimatedPdMm * irisMm) / assumedIrisMm;

/** Tenths of a millimetre, rounded half up: the product's rule for PD strings. */
export const pdString = (mm: number): string => {
  const tenths = Math.floor(mm * 10 + 0.5 + 1e-9);
  return `${Math.floor(tenths / 10)}.${tenths % 10}`;
};
